import { useEffect, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubscription,
  getSubscription,
  getSubscriptionLookups,
  listSubscriptions,
  updateSubscription,
} from '@/api/onsiteLiveSubscriptions';
import { useAuth } from '@/auth/useAuth';
import { formatDate } from '@/lib/dateFormat';
import type { Subscription, SubscriptionLookups } from '@/types/oscar';
import { ErrorBanner, FieldError } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';
import {
  emptySubscription,
  subscriptionSchema,
  subscriptionToForm,
  toSubscriptionPayload,
  type SubscriptionFormData,
} from './schema';
import { SubscriptionParamPicker, type PickerKind } from './SubscriptionParamPicker';

type Selected = { mode: 'edit'; id: number } | { mode: 'new'; copyOf?: Subscription } | null;

/**
 * Subscriptions - OnBudget's "Subscription" screen (Migration Plan section 8). Each row
 * is a data-driven SSRS subscription: report, recipients, render format and the four
 * parameters the report reads. Helpdesk reads; an admin edits once the subscriptions
 * cutover has made Oscar the owner (until then OnBudget does, and saves are refused).
 */
export function SubscriptionsTab() {
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selected, setSelected] = useState<Selected>(null);

  const lookupsQuery = useQuery({ queryKey: ['onsitelive-admin', 'subscription-lookups'], queryFn: ({ signal }) => getSubscriptionLookups(signal) });
  const listQuery = useQuery({
    queryKey: ['onsitelive-admin', 'subscriptions', { search: search.trim(), groupId, includeInactive }],
    queryFn: ({ signal }) => listSubscriptions({ search, groupId: groupId ? Number(groupId) : null, includeInactive }, signal),
  });
  const lookups = lookupsQuery.data;
  const canWrite = isAdmin && lookups?.WritesOpen === true;
  const rows = listQuery.data ?? [];

  if (lookupsQuery.isError) return <ErrorBanner message={(lookupsQuery.error as Error).message || 'Could not load subscriptions.'} />;

  return (
    <div className="space-y-3">
      {lookups && !lookups.WritesOpen && (
        <div role="status" className="rounded border border-orange/40 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <span className="font-semibold text-orange">Read only until the subscriptions cutover. </span>
          {lookups.Reason}
        </div>
      )}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="lg:w-96 lg:shrink-0">
          <div className="flex items-end gap-2">
            <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
              Search
              <input
                aria-label="Search subscriptions"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Description, report, recipient, customer or id"
                className={`${inputCls} mt-1 font-normal`}
              />
            </label>
            {canWrite && (
              <button type="button" className={btnSecondary} onClick={() => setSelected({ mode: 'new' })}>
                New subscription
              </button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              aria-label="Subscription group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className={`${inputCls} text-xs`}
            >
              <option value="">All groups</option>
              {(lookups?.Groups ?? []).map((g) => (
                <option key={g.Id} value={String(g.Id)}>{g.Name ?? `Group ${g.Id}`}{g.Inactive ? ' (inactive)' : ''}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1.5 text-xs text-darkGrey">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Include inactive
            </label>
          </div>

          {listQuery.isError && <ErrorBanner message={(listQuery.error as Error).message || 'Could not load subscriptions.'} />}
          <ul className="mt-2 max-h-[70vh] space-y-1 overflow-y-auto" aria-label="Subscriptions">
            {listQuery.isLoading && <li className="text-sm text-darkGrey/60">Loading…</li>}
            {!listQuery.isLoading && rows.length === 0 && <li className="text-sm text-darkGrey/60">Nothing matches.</li>}
            {rows.map((s) => {
              const isSel = selected?.mode === 'edit' && selected.id === s.SubscriptionId;
              return (
                <li key={s.SubscriptionId}>
                  <button
                    type="button"
                    onClick={() => setSelected({ mode: 'edit', id: s.SubscriptionId })}
                    aria-current={isSel ? 'true' : undefined}
                    className={`w-full rounded border px-2 py-1.5 text-left text-sm ${isSel ? 'border-navy bg-[#E6ECF2]' : 'border-border bg-white hover:bg-[#F4F6F9]'}`}
                  >
                    <span className="font-medium text-navy">{s.Description}</span>
                    {!s.Active && <span className="ml-2 rounded-full bg-darkGrey/10 px-1.5 text-[10px] font-semibold text-darkGrey/70">Inactive</span>}
                    <span className="block text-xs text-darkGrey/70">
                      #{s.SubscriptionId} · {s.ReportName}{s.GroupName ? ` · ${s.GroupName}` : ''}
                      {s.RenderFormat ? ` · ${s.RenderFormat}` : ''} · {s.Recipients} recipient{s.Recipients === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {rows.length >= 500 && <p className="mt-1 text-xs text-darkGrey/60">Showing the first 500 — narrow the search.</p>}
        </div>

        <div className="min-w-0 flex-1">
          {selected === null && (
            <p className="rounded border border-dashed border-border bg-white px-4 py-6 text-sm text-darkGrey/70">
              Pick a subscription on the left{canWrite ? ', or add a new one' : ''}.
            </p>
          )}
          {selected?.mode === 'new' && (
            <SubscriptionEditor
              key={`new-${selected.copyOf?.SubscriptionId ?? ''}`}
              subscription={null}
              copyOf={selected.copyOf}
              lookups={lookups}
              canWrite={canWrite}
              onSaved={(s) => setSelected({ mode: 'edit', id: s.SubscriptionId })}
              onCopy={() => undefined}
            />
          )}
          {selected?.mode === 'edit' && (
            <SubscriptionLoader
              key={selected.id}
              id={selected.id}
              lookups={lookups}
              canWrite={canWrite}
              onCopy={(s) => setSelected({ mode: 'new', copyOf: s })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function SubscriptionLoader({
  id, lookups, canWrite, onCopy,
}: { id: number; lookups: SubscriptionLookups | undefined; canWrite: boolean; onCopy: (s: Subscription) => void }) {
  const q = useQuery({ queryKey: ['onsitelive-admin', 'subscription', id], queryFn: ({ signal }) => getSubscription(id, signal) });
  if (q.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (q.isError || !q.data) return <ErrorBanner message={(q.error as Error | null)?.message || 'Could not load the subscription.'} />;
  return <SubscriptionEditor subscription={q.data} lookups={lookups} canWrite={canWrite} onCopy={() => onCopy(q.data)} />;
}

/** The OnBudget detail form, field for field. A copy starts as a new subscription with the same values. */
function SubscriptionEditor({
  subscription, copyOf, lookups, canWrite, onSaved, onCopy,
}: {
  subscription: Subscription | null;
  copyOf?: Subscription;
  lookups: SubscriptionLookups | undefined;
  canWrite: boolean;
  onSaved?: (s: Subscription) => void;
  onCopy: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [picker, setPicker] = useState<PickerKind | null>(null);
  const start = subscription ?? copyOf ?? null;
  // A new subscription starts on PDF (OnBudget defaulted render format 1 = Excel; PDF is the column default).
  const defaults = start ? subscriptionToForm(start) : { ...emptySubscription, RenderFormatId: String(lookups?.RenderFormats.find((f) => f.Code === 'PDF')?.Id ?? '') };
  const { register, handleSubmit, formState, reset, setValue, watch } = useForm<SubscriptionFormData>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: defaults,
  });
  useEffect(() => { if (subscription) reset(subscriptionToForm(subscription)); }, [subscription, reset]);

  const save = useMutation({
    mutationFn: (d: SubscriptionFormData) => {
      const body = toSubscriptionPayload(d);
      return subscription ? updateSubscription(subscription.SubscriptionId, body) : createSubscription(body);
    },
    onSuccess: (s) => {
      setError(null);
      setSavedAt(new Date().toISOString());
      qc.setQueryData(['onsitelive-admin', 'subscription', s.SubscriptionId], s);
      void qc.invalidateQueries({ queryKey: ['onsitelive-admin', 'subscriptions'] });
      onSaved?.(s);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the subscription.'),
  });
  const err = formState.errors;
  const param1 = watch('Param1');
  const pickerFor = (kind: PickerKind, name: 'Param1' | 'Param2' | 'Param3' | 'Param4') =>
    picker === kind && (
      <SubscriptionParamPicker
        kind={kind}
        value={watch(name)}
        customer={param1}
        onApply={(v) => setValue(name, v, { shouldDirty: true, shouldValidate: true })}
        onClose={() => setPicker(null)}
      />
    );

  return (
    <form
      noValidate
      className="rounded border border-border bg-white p-4"
      onSubmit={(e) => { e.preventDefault(); void handleSubmit((d) => save.mutate(d))(); }}
      aria-label={subscription ? `Subscription ${subscription.SubscriptionId}` : 'New subscription'}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-navy">
          {subscription ? subscription.Description : copyOf ? `New subscription (copy of #${copyOf.SubscriptionId})` : 'New subscription'}
          {subscription && <span className="ml-2 font-mono text-xs font-normal text-darkGrey/60">#{subscription.SubscriptionId}</span>}
        </h2>
        <div className="flex items-center gap-2">
          {subscription && (
            <p className="text-xs text-darkGrey/60">
              Added {formatDate(subscription.CreatedAt)} by {subscription.CreatedBy}
              {subscription.ModifiedAt && ` · changed ${formatDate(subscription.ModifiedAt)} by ${subscription.ModifiedBy ?? 'unknown'}`}
            </p>
          )}
          {subscription && canWrite && <button type="button" className={btnSecondary} onClick={onCopy}>Copy</button>}
        </div>
      </div>

      <fieldset disabled={!canWrite || save.isPending} className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-darkGrey md:col-span-2">
          <input type="checkbox" {...register('Active')} />
          Active — SSRS sends this subscription
        </label>
        <Text label="Description" reg={register('Description')} error={err.Description?.message} />
        <Text label="Report name" reg={register('ReportName')} error={err.ReportName?.message} hint="The SSRS report this subscription runs." />
        <Area label="To" reg={register('To')} error={err.To?.message} hint="Email addresses, separated by ; — customers' addresses are fine." wide />
        <Area label="CC" reg={register('CC')} error={err.CC?.message} />
        <Area label="BCC" reg={register('BCC')} error={err.BCC?.message} />
        <div className="flex flex-col text-xs font-semibold text-darkGrey">
          <label className="flex flex-col">
            Render format
            <select {...register('RenderFormatId')} className={`${inputCls} mt-1 font-normal`}>
              <option value="">—</option>
              {(lookups?.RenderFormats ?? []).map((f) => <option key={f.Id} value={String(f.Id)}>{f.Description}</option>)}
            </select>
          </label>
          {err.RenderFormatId && <FieldError message={err.RenderFormatId.message} />}
        </div>
        <div className="flex flex-col text-xs font-semibold text-darkGrey">
          <label className="flex flex-col">
            Subscription group
            <select {...register('SubscriptionGrp')} className={`${inputCls} mt-1 font-normal`}>
              <option value="">— none —</option>
              {(lookups?.Groups ?? []).map((g) => (
                <option key={g.Id} value={String(g.Id)}>{g.Name ?? `Group ${g.Id}`}{g.Inactive ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </label>
        </div>
        <Text label="Subject" reg={register('Subject')} error={err.Subject?.message} wide />
        <Area label="Comment" reg={register('Comment')} error={err.Comment?.message} hint="The email body." wide />

        <Param label="Parameter 1 — customer code" reg={register('Param1')} error={err.Param1?.message}
               canWrite={canWrite} onPick={() => setPicker(picker === 'customer' ? null : 'customer')} picker={pickerFor('customer', 'Param1')} />
        <Param label="Parameter 2 — customer sites" reg={register('Param2')} error={err.Param2?.message}
               canWrite={canWrite} onPick={() => setPicker(picker === 'sites' ? null : 'sites')} picker={pickerFor('sites', 'Param2')} />
        <Param label="Parameter 3 — branches, areas or regions" reg={register('Param3')} error={err.Param3?.message}
               canWrite={canWrite} onPick={() => setPicker(picker === 'branch' ? null : 'branch')} picker={pickerFor('branch', 'Param3')} />
        <Param label="Parameter 4 — account managers" reg={register('Param4')} error={err.Param4?.message}
               canWrite={canWrite} onPick={() => setPicker(picker === 'salesreps' ? null : 'salesreps')} picker={pickerFor('salesreps', 'Param4')} />

        <Area label="Development notes" reg={register('DevelopmentNotes')} wide />
      </fieldset>

      {subscription && (subscription.SqlView || subscription.ReplyTo || subscription.Priority) && (
        <details className="mt-3 text-xs text-darkGrey/80">
          <summary className="cursor-pointer font-semibold text-darkGrey">Legacy settings (not edited here)</summary>
          <dl className="mt-1 grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1">
            <dt>Render code</dt><dd className="font-mono">{subscription.RenderFormat ?? '—'}</dd>
            <dt>Include report</dt><dd>{subscription.IncludeReport ?? '—'}</dd>
            <dt>Include link</dt><dd>{subscription.IncludeLink ?? '—'}</dd>
            <dt>Priority</dt><dd>{subscription.Priority ?? '—'}</dd>
            <dt>Reply to</dt><dd>{subscription.ReplyTo || '—'}</dd>
            <dt>SQL view</dt><dd className="whitespace-pre-wrap font-mono">{subscription.SqlView || '—'}</dd>
          </dl>
        </details>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {canWrite && (
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : subscription ? 'Save changes' : 'Add subscription'}
          </button>
          {savedAt && !save.isPending && <span className="text-xs text-green" role="status">Saved.</span>}
        </div>
      )}
    </form>
  );
}

function Text({ label, reg, error, hint, wide }: { label: string; reg: UseFormRegisterReturn; error?: string; hint?: string; wide?: boolean }) {
  return (
    <div className={`flex flex-col text-xs font-semibold text-darkGrey ${wide ? 'md:col-span-2' : ''}`}>
      <label className="flex flex-col">
        {label}
        <input {...reg} className={`${inputCls} mt-1 font-normal`} />
      </label>
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
      {error && <FieldError message={error} />}
    </div>
  );
}

function Area({ label, reg, error, hint, wide }: { label: string; reg: UseFormRegisterReturn; error?: string; hint?: string; wide?: boolean }) {
  return (
    <div className={`flex flex-col text-xs font-semibold text-darkGrey ${wide ? 'md:col-span-2' : ''}`}>
      <label className="flex flex-col">
        {label}
        <textarea {...reg} rows={2} className={`${inputCls} mt-1 font-normal`} />
      </label>
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
      {error && <FieldError message={error} />}
    </div>
  );
}

/** A parameter: the text OnBudget stored, editable, plus a picker that writes the same format. */
function Param({
  label, reg, error, canWrite, onPick, picker,
}: {
  label: string;
  reg: UseFormRegisterReturn;
  error?: string;
  canWrite: boolean;
  onPick: () => void;
  picker: React.ReactNode;
}) {
  return (
    <div className="flex flex-col text-xs font-semibold text-darkGrey">
      <div className="flex items-end gap-2">
        <label className="flex flex-1 flex-col">
          {label}
          <input {...reg} className={`${inputCls} mt-1 font-mono font-normal`} />
        </label>
        {canWrite && <button type="button" className={btnSecondary} onClick={onPick} aria-label={`Pick ${label.toLowerCase()}`}>Pick…</button>}
      </div>
      {picker}
      {error && <FieldError message={error} />}
    </div>
  );
}
